import Close from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { addChartToApp } from "@/features/app-studio/api/appChartsApi";
import { useAddToAppTarget } from "@/features/app-studio/hooks/useAddToAppTarget";
import { AddToAppTargetSelect } from "@/features/app-studio/ui/AddToAppTargetSelect";
import { AppChartColumnSpanSelect } from "@/features/app-studio/ui/AppChartColumnSpanSelect";
import { APP_CHART_GRID_COLUMNS } from "@/features/app-studio/lib/appChartLayout";
import type { GeneralizedChartDefinition } from "@/features/chat/model/chatTypes";

type AddChartToAppDialogProps = {
  open: boolean;
  onClose: () => void;
  chart: GeneralizedChartDefinition | null;
  sql: string | null;
};

export function AddChartToAppDialog({ open, onClose, chart, sql }: AddChartToAppDialogProps) {
  const { t } = useTranslation(["appStudio", "chat"]);
  const navigate = useNavigate();
  const target = useAddToAppTarget(open);
  const { reset: resetTarget, selectedAppId, writeAppId, tabIdForPayload, isTargetComplete } =
    target;

  const [columnSpan, setColumnSpan] = useState<number>(APP_CHART_GRID_COLUMNS);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    resetTarget();
    setColumnSpan(APP_CHART_GRID_COLUMNS);
    setIsSaving(false);
    setSaveError(null);
  }, [resetTarget]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (!chart || !sql || selectedAppId === "" || writeAppId === null || !isTargetComplete) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await addChartToApp(writeAppId, {
        sql,
        chart_definition: chart,
        column_span: columnSpan,
        tab_id: tabIdForPayload,
      });
      onClose();
      navigate(`/app-studio/${selectedAppId}`, { state: { activeTabId: tabIdForPayload } });
    } catch (error) {
      const message = error instanceof Error ? error.message : t("addChart.errors.saveFailed");
      setSaveError(message);
      setIsSaving(false);
    }
  };

  const chartTitle = chart?.title ?? chart?.id ?? t("chat:response.tabs.chart");

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || isSaving) {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
            border: "1px solid var(--theme-border)",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {t("appStudio:addChart.title")}
        </Typography>
        <IconButton
          aria-label={t("appStudio:addChart.closeAria")}
          onClick={handleClose}
          disabled={isSaving}
          size="small"
          edge="end"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
            {t("appStudio:addChart.description", { chartTitle })}
          </Typography>

          {!sql ? (
            <Alert severity="warning">{t("appStudio:addChart.noSql")}</Alert>
          ) : null}

          <AddToAppTargetSelect
            idPrefix="add-chart"
            target={target}
            disabled={isSaving || !sql}
            onTargetChange={() => setSaveError(null)}
          />

          <AppChartColumnSpanSelect
            value={columnSpan}
            disabled={isSaving || !sql}
            onChange={setColumnSpan}
          />

          {saveError ? (
            <Alert severity="error" icon={<WarningAmber fontSize="inherit" />}>
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--theme-border)" }}>
        <Button onClick={handleClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          {t("appStudio:addChart.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={isSaving || !sql || !chart || !isTargetComplete}
          sx={{ textTransform: "none" }}
        >
          {isSaving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>{t("appStudio:addChart.adding")}</span>
            </Stack>
          ) : (
            t("appStudio:addChart.confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
